import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, CloudUpload } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import Pagination from "../components/Paginate";
import axiosInstance from '../utils/axiosInstance';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import LoadingSpinner from '../components/LoadingSpinner';
import 'react-confirm-alert/src/react-confirm-alert.css';

const NotificationTemplates = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const navigate = useNavigate();

  const defaultFilters = useMemo(() => ({
    search: searchParams.get('search') || "",
    page: parseInt(searchParams.get('page')) || 1,
    limit: parseInt(searchParams.get('limit')) || 10,
  }), []);

  const [filters, setFilters] = useState(defaultFilters);
  const [pagination, setPagination] = useState({
    currentPage: defaultFilters.page,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: defaultFilters.limit
  });

  const buildQueryString = useCallback((currentFilters) => {
    const queryParams = new URLSearchParams();
    if (currentFilters.search) queryParams.append('search', currentFilters.search);
    if (currentFilters.page) queryParams.append('page', currentFilters.page.toString());
    if (currentFilters.limit) queryParams.append('limit', currentFilters.limit.toString());
    return queryParams.toString();
  }, []);

  const fetchTemplates = useCallback(async (queryString) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`notification-template/list?${queryString}`);
      const { templates: newTemplates, pagination: newPagination } = response.data.data.data;
      setTemplates(newTemplates);
      setPagination(prev => ({
        ...prev,
        currentPage: newPagination.currentPage,
        totalPages: newPagination.totalPages,
        totalItems: newPagination.totalItems
      }));
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Error fetching templates');
    } finally {
      setLoading(false);
    }
  }, []);
  const getSerialNumber = (index) => {
    return ((pagination.currentPage - 1) * pagination.itemsPerPage) + index + 1;
  };
  useEffect(() => {
    const queryString = buildQueryString(filters);
    setSearchParams(queryString, { replace: true });
    fetchTemplates(queryString);
  }, [filters, buildQueryString, fetchTemplates, setSearchParams]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setFilters(prev => ({ ...prev, search: value, page: 1 }));
      }, 500),
    []
  );

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleClearFilters = useCallback(() => {
    setFilters(defaultFilters);
    setLocalSearch("");
    debouncedSearch.cancel();
  }, [defaultFilters, debouncedSearch]);

  const handlePageChange = useCallback((newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  }, []);


  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Notification Templates</h1>
        <div className="flex items-center gap-4">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Clear Filters
          </button>
      
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={localSearch}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div><LoadingSpinner /></div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
              <th className="px-6 py-3 text-left">S.No</th>
                <th className="px-6 py-3 text-left">Template Name</th>
                <th className="px-6 py-3 text-left">Action</th>

               
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates?.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-gray-500">
                    No templates found
                  </td>
                </tr>
              ) : (
                templates?.map((template,index) => (
                  <tr key={template.id}>
                    <td className="px-6 py-4">{getSerialNumber(index)}</td>
                    <td className="px-6 py-4">{template.templateKey}</td>
                    <td>
                    <button
    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
    onClick={() =>
      navigate(
  
        `/notifficationTemplate/${template.templateKey}`
      )
    }
  >
    Edit
  </button>
  </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          forcePage={pagination.currentPage - 1}
        />
      )}
    </div>
  );
};

export default NotificationTemplates;